import { Component, computed, effect, inject, signal, DestroyRef } from '@angular/core';
import { DatePipe } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs/operators';
import { SidebarService } from '../../services/sidebar.service';
import { SidebarPanelType } from '../../models/sidebar-panel';
import { SalesOrderService, SalesOrderFullDetail } from '../../../features/sales-orders/services/sales-order.service';
import { getOrderStatusLabel } from '../../constants/domain.constants';
import { LoadingOverlayService } from '../../services/loading-overlay.service';

@Component({
  selector: 'app-sales-order-detail-panel',
  imports: [DatePipe],
  templateUrl: './sales-order-detail-panel.component.html',
  styleUrl: './sales-order-detail-panel.component.scss'
})
export class SalesOrderDetailPanelComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly sidebarService = inject(SidebarService);
  private readonly salesOrderService = inject(SalesOrderService);
  private readonly loadingOverlay = inject(LoadingOverlayService);

  readonly order = computed(() => {
    const panel = this.sidebarService.currentPanel();
    if (panel?.type === SidebarPanelType.SalesOrderDetail) {
      return panel.data;
    }
    return null;
  });

  readonly fullDetail = signal<SalesOrderFullDetail | null>(null);
  readonly detailLoading = signal(false);

  readonly statusLabel = computed(() => {
    const o = this.order();
    return o ? getOrderStatusLabel(o.status) : '';
  });

  constructor() {
    effect(() => {
      const o = this.order();
      if (o?.number) {
        this.detailLoading.set(true);
        this.fullDetail.set(null);
        this.salesOrderService.getFullDetail(o.number)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: (detail) => {
              this.fullDetail.set(detail);
              this.detailLoading.set(false);
            },
            error: () => this.detailLoading.set(false)
          });
      }
    });
  }

  downloadPod(): void {
    const detail = this.fullDetail();
    if (detail) {
      this.loadingOverlay.show('Getting proof of delivery. Please do not refresh.');
      this.salesOrderService.downloadPod(detail.id).pipe(
        finalize(() => this.loadingOverlay.hide())
      ).subscribe((blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `POD-${detail.number}.pdf`;
        link.click();
        URL.revokeObjectURL(url);
      });
    }
  }
}
